<script setup lang="ts">
import Icon from '~/components/Icon/Legacy/index.vue'

interface Item {
  title?: string
  text?: string
  icon?: string
  imageUrl?: string
  beta?: boolean
  url?: string
  handler?: () => void
}

interface Props {
  type?: 'small' | 'big'
  title?: string
  textBtnSeeAll?: string
  iconBtnSeeAll?: string
  urlBtnSeeAll?: string
  items?: Item[]
}

const props = defineProps<Props>()

const NuxtLink = resolveComponent('NuxtLink')

function isExternalUrl(url?: string) {
  return url?.startsWith('http') || url?.startsWith('//')
}

const isExternalLink = computed(() => isExternalUrl(props.urlBtnSeeAll))
</script>

<template>
  <div class="header-side-container">
    <div class="header-side-content">
      <div class="title">
        <h3>{{ props?.title }}</h3>
        <NuxtLink
          v-if="props?.textBtnSeeAll"
          class="button-see-all"
          :to="urlBtnSeeAll"
          :target="isExternalLink ? '_blank' : undefined"
        >
          {{ props?.textBtnSeeAll }}
          <Icon v-if="props?.iconBtnSeeAll" :name="props?.iconBtnSeeAll" />
        </NuxtLink>
      </div>

      <div class="items" :class="props?.type">
        <component
          :is="btn?.url ? NuxtLink : 'button'"
          v-for="(btn, index) in props?.items"
          :key="index"
          :to="btn?.url"
          :target="isExternalUrl(btn?.url) ? '_blank' : undefined"
          class="item"
          @click="btn?.handler?.()"
        >
          <Icon v-if="btn?.icon" :name="btn?.icon" />
          <div
            v-if="btn?.imageUrl"
            class="image"
            :style="{
              backgroundImage: `url(${btn?.imageUrl})`,
              backgroundPosition: 'center',
              backgroundSize: 'cover',
            }"
          />
          <div gap-1 class="text">
            <h5>{{ btn?.title }} <span v-if="btn?.beta">beta</span></h5>
            <p>{{ btn?.text }}</p>
          </div>
        </component>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.header-side-container {
  --at-apply: bg-gray-lighter;
  width: 100%;
  // max-height: 350px;
  // min-height: 350px;
  padding: 28px 0 0 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 0 0 20px 0;
    padding: 0 0 0 28px;
    max-width: 300px;

    h3 {
      --at-apply: text-soft-black;
      font-family: 'Circular Std Bold';
      font-size: 16px;
      letter-spacing: -0.64px;
      font-feature-settings: 'ss02' on, 'ss01' on, 'salt' on;
    }

    .button-see-all {
      --at-apply: text-purple;
      display: flex;
      align-items: center;
      gap: 5px;
      font-family: 'Circular Std Bold';
      font-size: 14px;
      font-weight: 700;
      line-height: 160%;
      letter-spacing: -0.48px;
      font-feature-settings: 'ss02' on, 'ss01' on,'salt' on;
      transition: 0.2s;

      &:hover {
        opacity: 0.8;
        svg {
          transform:  rotate(-90deg) translateY(2px) scale(1.1);
        }
      }

      svg {
        --at-apply: fill-purple;
        width: 8px;
        height: 8px;
        transform: rotate(-90deg);
        transition: 0.2s all;
      }
    }
  }

  .items {
    max-width: 300px;
    max-height: 300px;
    display: flex;
    flex-direction: column;
    overflow-y: scroll;

    &::-webkit-scrollbar {
      display: none;
    }

    .item {
      display: flex;
      align-items: center;
      text-align: left;
      gap: 12px;
      padding: 10px 28px;
      border-radius: 5px;
      transition: 0.2s all;

      &:hover {
        --at-apply: bg-light-bluish;
      }

      svg {
        width: 15px;
        min-width: 15px;
        margin: 3px 0 0 0;
        --at-apply: fill-soft-black;
      }

      .image {
        width: 60px;
        min-width: 60px;
        height: 60px;
        min-height: 60px;
        background-repeat: no-repeat;
        background-position: center;
        background-size: cover;
        border-radius: 8px;
      }

      .text {
        display: flex;
        flex-direction: column;
        justify-content: space-between;

        h5 {
          --at-apply: text-soft-black;
          font-family: 'Inter';
          font-size: 12px;
          font-weight: 700;
          font-feature-settings: 'cv09' on, 'cv01' on;

          span {
            --at-apply: bg-purple-2 text-dark-purple;
            font-family: 'Inter';
            font-size: 10px;
            font-weight: 400;
            padding: 6px 10px;
            border-radius: 999px;
            margin: 0 0 0 5px
          }
        }

        p {
          --at-apply: text-bluish-darker;
          font-family: 'Inter';
          font-size: 10px;
          font-variant-numeric: lining-nums proportional-nums;
          font-feature-settings: 'cv01' on, 'cv09' on;
        }
      }
    }

    &.big {
      // gap: 0;
      .item {
        --at-apply: border-b border-ligh-bluish;
        position: relative;
        align-items: center;
        padding: 20px 28px;
        .image {
          width: 80px;
          min-width: 80px;
          height: 80px;
          min-height: 80px;
        }
        .text {
          display: flex;
          flex-direction: column;
          justify-content: center;
        }
      }
    }

    &.small {
      .item {
        align-items: flex-start;
        gap: 10px;
        height: 100px;
        .text {
          display: flex;
          flex-direction: column;
        }
      }
    }
  }
}
</style>
